import { useState, useMemo, useCallback } from 'react';
import useAsyncRetry from 'react-use/esm/useAsyncRetry';
import { workflowApiRef, Substitutions } from '../api';
import { useApi, errorApiRef } from '@backstage/core-plugin-api';
import { calculateBuildTimeDuration } from './Workflow/utils/calculateBuildTimeDuration';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

export type WorkflowRun = {
  id: string;
  message: string;
  url?: string;
  googleUrl?: string;
  status: string;
  substitutions: Substitutions;
  createTime: string;
  duration: string;
  rerun: () => Promise<void>;
  source?: {
    branchName: string;
    commit: {
      hash: string;
      url: string;
    };
  };
};

export function useWorkflowRuns(options: {
  projectId: string;
  location: string;
  cloudBuildFilter: string;
}) {
  const { projectId, location, cloudBuildFilter } = options;
  const api = useApi(workflowApiRef);
  const errorApi = useApi(errorApiRef);
  const { entity } = useEntity();

  const [total, setTotal] = useState(0);
  const [isRerunning, setIsRerunning] = useState<Record<string, boolean>>({});

  const createRerunHandler = useCallback(
    (runId: string) => {
      return async () => {
        if (isRerunning[runId]) return;

        setIsRerunning(prev => ({ ...prev, [runId]: true }));
        try {
          await api.reRunWorkflow({
            projectId,
            location,
            runId,
            entityRef: stringifyEntityRef(entity),
          });
        } catch (e) {
          errorApi.post(e as Error);
        } finally {
          setIsRerunning(prev => ({ ...prev, [runId]: false }));
        }
      };
    },
    [isRerunning, api, projectId, location, entity, errorApi],
  );

  const fetchWorkflowRuns = useCallback(async () => {
    if (!projectId || !location) {
      return [];
    }
    const workflowRunsData = await api.listWorkflowRuns({
      projectId,
      location,
      cloudBuildFilter,
      entityRef: stringifyEntityRef(entity),
    });

    const runs = workflowRunsData.builds.map(run => ({
      message: run.substitutions.REPO_NAME,
      id: run.id,
      rerun: createRerunHandler(run.id),
      substitutions: run.substitutions,
      source: {
        branchName: run.substitutions.REPO_NAME,
        commit: {
          hash: run.substitutions.COMMIT_SHA,
          url: run.substitutions.REPO_NAME,
        },
      },
      status: run.status,
      url: run.logUrl,
      googleUrl: run.logUrl,
      duration: calculateBuildTimeDuration(
        run.timing?.BUILD?.startTime ?? '',
        run.timing?.BUILD?.endTime ?? '',
      ),
      createTime:
        typeof run.createTime === 'object'
          ? run.createTime.seconds
          : run.createTime,
    }));

    setTotal(runs.length);
    return runs;
  }, [projectId, location, api, cloudBuildFilter, entity, createRerunHandler]);

  const {
    loading,
    value: allRuns,
    retry,
    error,
  } = useAsyncRetry(fetchWorkflowRuns, [fetchWorkflowRuns]);

  // Calculate paginated data
  const paginatedRuns = useMemo(() => {
    if (!allRuns) return undefined;
    return allRuns;
  }, [allRuns]);

  const handleRetry = useCallback(() => {
    if (api.invalidateCache) {
      api.invalidateCache();
    }
    retry();
  }, [api, retry]);

  return [
    {
      loading,
      runs: paginatedRuns,
      projectName: `${projectId}`,
      total,
      error,
      isRerunning,
    },
    {
      retry: handleRetry,
    },
  ] as const;
}
